import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion2';
import Title from '@/components/ui/Title';
import { Plus } from 'lucide-react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Label } from '@/components/ui/label';
import type {
  QuizAnswerOption,
  QuizAnswerOptionInput,
  QuizQuestionInput,
  QuizTemplate,
  QuizTemplateInput,
} from '@/features/webinar/types';
import QuizForm from './QuizForm';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import {
  createQuizAnswerOption,
  createQuizQuestion,
  createQuizTemplate,
  deleteQuizAnswerOption,
  deleteQuizQuestion,
  deleteQuizTemplate,
  fetchQuizTemplate,
  listQuizTemplates,
  updateQuizAnswerOption,
  updateQuizQuestion,
  updateQuizTemplate,
} from '@/features/webinar/webinarThunks';
import { Skeleton } from '@/components/ui/skeleton';
import EmptyInputFile from '../EmptyInputFile';
import { toast } from 'react-toastify';
import { mapErrorMessage } from '@/lib/mapApiError';
import type { AppError } from '@/features/auth/types';
import { useTranslation } from 'react-i18next';
import TimeCodeComponent, { type TimeCodeScheduling } from '../../evergreen/TimeCodeComponent';
import { getEvergreenTimelineResourceIds } from '@/features/webinar/evergreenTimelineUtils';
import { mergeInteractionFormStates } from '@/lib/interactionFormStates';
import { cn } from '@/lib/utils';
import type {
  InteractionGroupHandle,
  InteractionItemHandle,
  SaveAllResult,
} from '../interactionBatch';

type QuizItem = {
  id: string;
  existingQuiz?: QuizTemplate;
};

type QuizsFormProps = {
  webinarId: number;
  workspaceId: number;
  isEdit?: boolean;
  isEvergreen?: boolean;
  scheduling?: TimeCodeScheduling;
  videoUrl?: string;
  videoFallbackUrl?: string;
  readOnly?: boolean;
  // Mode sauvegarde globale : pas de bouton par quizz, le parent pilote saveAll.
  batchMode?: boolean;
  // Remonte l'état « modifications non enregistrées » du groupe au parent.
  onDirtyChange?: (dirty: boolean) => void;
  openAccordionTrigger?: boolean;
  onAccordionOpened?: () => void;
};

const QUIZ_ACCORDION_VALUE = 'accordion-1';

const QuizsForm = forwardRef<InteractionGroupHandle, QuizsFormProps>(function QuizsForm(
  {
    webinarId,
    workspaceId,
    isEdit,
    isEvergreen,
    scheduling,
    videoUrl,
    videoFallbackUrl,
    readOnly,
    batchMode = false,
    onDirtyChange,
    openAccordionTrigger,
    onAccordionOpened,
  },
  ref,
) {
  const locked = readOnly === true;
  const { t } = useTranslation('createWebinar');
  const dispatch = useAppDispatch();
  const quizTemplates = useAppSelector((state: RootState) => state.webinar.quizTemplates);
  const quizTemplatesLoading = useAppSelector(
    (state: RootState) => state.webinar.quizTemplatesLoading,
  );
  const evergreenTimeline = useAppSelector((state: RootState) => state.webinar.evergreenTimeline);
  const evergreenQuizIds = useMemo(
    () =>
      isEvergreen && !scheduling
        ? getEvergreenTimelineResourceIds(evergreenTimeline, 'QUIZ')
        : null,
    [isEvergreen, scheduling, evergreenTimeline],
  );
  const [quizzes, setQuizzes] = useState<QuizItem[]>([]);
  const pendingOperationsRef = useRef<Set<string>>(new Set());
  const [mainAccordionOpen, setMainAccordionOpen] = useState<string>('');
  const [innerAccordionValue, setInnerAccordionValue] = useState<string>('');

  useEffect(() => {
    if (openAccordionTrigger) {
      setMainAccordionOpen(QUIZ_ACCORDION_VALUE);
      onAccordionOpened?.();
    }
  }, [openAccordionTrigger, onAccordionOpened]);
  const [submittingQuizzes, setSubmittingQuizzes] = useState<Record<string, boolean>>({});

  // Handles impératifs des formulaires enfants, indexés par id de quizz.
  const itemRefs = useRef<Map<string, InteractionItemHandle | null>>(new Map());
  const [dirtyMap, setDirtyMap] = useState<Record<string, boolean>>({});

  const handleChildDirty = useCallback((quizItemId: string, dirty: boolean) => {
    setDirtyMap((prev) => (prev[quizItemId] === dirty ? prev : { ...prev, [quizItemId]: dirty }));
  }, []);

  const hasDirty = quizzes.some((quiz) => dirtyMap[quiz.id]);

  useEffect(() => {
    onDirtyChange?.(hasDirty);
  }, [hasDirty, onDirtyChange]);

  const handleInnerAccordionChange = (value: string | undefined) => {
    setInnerAccordionValue(value || '');
  };

  useEffect(() => {
    if (webinarId && workspaceId) {
      dispatch(listQuizTemplates({ workspaceId, webinarId }));
    }
  }, [workspaceId, webinarId]);

  useEffect(() => {
    if (quizTemplatesLoading) return;

    let filteredTemplates = quizTemplates.filter((template) => {
      const thankyouoffer = (template as any).thankyouoffer;
      const waitingRoomQuiz = template.waitingRoomQuiz;
      return (
        (thankyouoffer === false || thankyouoffer === undefined || thankyouoffer === null) &&
        (waitingRoomQuiz === false || waitingRoomQuiz === undefined || waitingRoomQuiz === null)
      );
    });

    if (evergreenQuizIds) {
      filteredTemplates = filteredTemplates.filter((t) => evergreenQuizIds.has(t.id));
    }

    const savingIds = new Set(
      Object.entries(submittingQuizzes)
        .filter(([, isSubmitting]) => isSubmitting)
        .map(([id]) => id),
    );

    setQuizzes((prevQuizzes) =>
      mergeInteractionFormStates(prevQuizzes, filteredTemplates, {
        toState: (template) => ({ id: String(template.id), existingQuiz: template }),
        isDraft: (quiz) => quiz.existingQuiz == null,
        savingIds,
      }),
    );
  }, [quizTemplatesLoading, quizTemplates, evergreenQuizIds, submittingQuizzes]);

  const addQuiz = () => {
    if (locked) return;
    if (quizzes.length >= 10) return;
    const newQuiz = { id: crypto.randomUUID() };
    setQuizzes((prev) => [...prev, newQuiz]);
    const newIndex = quizzes.length;
    setInnerAccordionValue(`item-${newIndex}`);
  };

  const removeQuiz = async (quizId: string) => {
    if (locked) return;
    const quizToRemove = quizzes.find((quiz) => quiz.id === quizId);
    const existingQuiz = quizToRemove?.existingQuiz;

    if (quizzes.length > 1) {
      setQuizzes((prev) => prev.filter((quiz) => quiz.id !== quizId));
    } else {
      setQuizzes([{ id: crypto.randomUUID() }]);
    }

    if (existingQuiz) {
      const templateId = existingQuiz.id;
      const question = existingQuiz.questions?.[0];
      try {
        if (templateId && question) {
          const questionId = question.id;
          if (question.options && question.options.length > 0) {
            for (const option of question.options) {
              await dispatch(
                deleteQuizAnswerOption({
                  workspaceId,
                  webinarId,
                  templateId,
                  questionId,
                  optionId: option.id,
                }),
              ).unwrap();
            }
          }

          await dispatch(
            deleteQuizQuestion({
              workspaceId,
              webinarId,
              templateId,
              questionId,
            }),
          ).unwrap();
        }

        if (templateId) {
          await dispatch(
            deleteQuizTemplate({
              workspaceId,
              webinarId,
              templateId,
            }),
          ).unwrap();
        }
      } catch (error) {
        console.error('Failed to delete quiz', error);
        return;
      }
    }
  };

  const removeAnswerOption = async (quizId: string, optionId: number) => {
    if (locked) return;
    console.log('removeAnswerOption', quizId, optionId);
    const quizEntry = quizzes.find((quiz) => quiz.id === quizId);
    const existingQuiz = quizEntry?.existingQuiz;
    if (!existingQuiz) {
      return;
    }

    const templateId = existingQuiz.id;
    const question = existingQuiz.questions?.[0];
    if (!templateId || !question || !optionId) {
      return;
    }

    try {
      await dispatch(
        deleteQuizAnswerOption({
          workspaceId,
          webinarId,
          templateId,
          questionId: question.id,
          optionId,
        }),
      ).unwrap();

      setQuizzes((prev) =>
        prev.map((quiz) =>
          quiz.id === quizId && quiz.existingQuiz
            ? {
                ...quiz,
                existingQuiz: {
                  ...quiz.existingQuiz,
                  questions:
                    quiz.existingQuiz.questions?.map((q) =>
                      q.id === question.id
                        ? {
                            ...q,
                            options: q.options?.filter((option) => option.id !== optionId) ?? [],
                          }
                        : q,
                    ) ?? [],
                },
              }
            : quiz,
        ),
      );
    } catch (error) {
      console.error('Failed to delete quiz answer option', error);
    }
  };

  const resolveColorValue = (value?: string, custom?: string | null) => {
    if (!value) {
      return null;
    }
    if (value === 'custom') {
      return custom ?? null;
    }
    if (value === 'no-border') {
      return null;
    }

    if (value === 'gradient1-color1') {
      return 'gradient1';
    }
    if (value === 'gradient2-color1') {
      return 'gradient2';
    }
    return value;
  };

  const handleQuizSubmit = async (
    quizData: any,
    quizId: string,
    options?: { silent?: boolean },
  ) => {
    if (locked) return;
    if (pendingOperationsRef.current.has(quizId)) {
      console.log('Quiz submission already in progress for', quizId);
      return;
    }

    try {
      pendingOperationsRef.current.add(quizId);
      setSubmittingQuizzes((prev) => ({ ...prev, [quizId]: true }));

      const quizEntry = quizzes.find((quiz) => quiz.id === quizId);
      const existingQuiz = quizEntry?.existingQuiz;
      const templateIdFromState = existingQuiz?.id;

      const templateInput: QuizTemplateInput = {
        name: quizData.name.trim(),
        borderColor: resolveColorValue(quizData.borderColor, quizData.customBorderColor),
        accentColor: resolveColorValue(quizData.accentColor, quizData.customAccentColor),
        backgroundColor: resolveColorValue(
          quizData.backgroundColor,
          quizData.customBackgroundColor,
        ),
        titleColor: quizData.titleColor ?? null,
        enabledDuringLive: quizData.enabledDuringLive,
        shuffleQuestions: false,
        shuffleOptions: false,
        published: false,
      };

      let finalTemplateId: number;

      let finalQuestionId: number;

      if (!templateIdFromState) {
        console.log('Creating new quiz', templateInput);
        finalTemplateId = await dispatch(
          createQuizTemplate({
            workspaceId,
            webinarId,
            input: templateInput,
          }),
        ).unwrap();

        if (!finalTemplateId) {
          console.error('Failed to create quiz template');
          return;
        }

        const questionPayload = quizData.question;
        if (!questionPayload?.textContent) {
          return;
        }

        const questionInput: QuizQuestionInput = {
          textContent: questionPayload.textContent.trim(),
          type: questionPayload.type,
          position: questionPayload.position ?? 0,
          points: questionPayload.points ?? null,
          timeLimitSeconds: questionPayload.timeLimitSeconds ?? null,
        };

        finalQuestionId = await dispatch(
          createQuizQuestion({
            workspaceId,
            webinarId,
            templateId: finalTemplateId,
            input: questionInput,
          }),
        ).unwrap();

        if (!finalQuestionId) {
          console.error('Failed to create quiz question');
          return;
        }

        const createdOptionTexts = new Set<string>();

        for (const option of questionPayload.options || []) {
          if (!option?.textContent?.trim()) {
            continue;
          }

          const normalizedText = option.textContent.trim().toLowerCase();
          if (createdOptionTexts.has(normalizedText)) {
            continue;
          }

          const optionInput: QuizAnswerOptionInput = {
            textContent: option.textContent.trim(),
            correct: option.correct ?? false,
            position: option.position ?? createdOptionTexts.size,
          };

          const optionId = await dispatch(
            createQuizAnswerOption({
              workspaceId,
              webinarId,
              templateId: finalTemplateId,
              questionId: finalQuestionId,
              input: optionInput,
            }),
          ).unwrap();

          if (optionId) {
            createdOptionTexts.add(normalizedText);
          }
        }

        const createdQuiz = await dispatch(
          fetchQuizTemplate({
            workspaceId,
            webinarId,
            templateId: finalTemplateId,
          }),
        ).unwrap();

        setQuizzes((prevQuizzes) =>
          prevQuizzes.map((quiz) =>
            quiz.id === quizId
              ? {
                  ...quiz,
                  existingQuiz: createdQuiz,
                }
              : quiz,
          ),
        );
        if (!options?.silent) {
          toast.success(t('quizzes.quizCreatedSuccess'));
        }
        setInnerAccordionValue('');
      } else {
        console.log('Updating existing quiz');
        finalTemplateId = templateIdFromState;

        const questionPayload = quizData.question;
        if (!questionPayload?.textContent) {
          return;
        }

        const currentQuiz = await dispatch(
          fetchQuizTemplate({
            workspaceId,
            webinarId,
            templateId: finalTemplateId,
          }),
        ).unwrap();

        await dispatch(
          updateQuizTemplate({
            workspaceId,
            webinarId,
            templateId: finalTemplateId,
            input: templateInput,
          }),
        ).unwrap();

        const existingQuestion = currentQuiz?.questions?.[0] || existingQuiz?.questions?.[0];
        const questionInput: QuizQuestionInput = {
          textContent: questionPayload.textContent.trim(),
          type: questionPayload.type,
          position: questionPayload.position ?? 0,
          points: questionPayload.points ?? null,
          timeLimitSeconds: questionPayload.timeLimitSeconds ?? null,
        };

        if (existingQuestion?.id) {
          finalQuestionId = existingQuestion.id;
          await dispatch(
            updateQuizQuestion({
              workspaceId,
              webinarId,
              templateId: finalTemplateId,
              questionId: existingQuestion.id,
              input: questionInput,
            }),
          ).unwrap();
        } else {
          finalQuestionId = await dispatch(
            createQuizQuestion({
              workspaceId,
              webinarId,
              templateId: finalTemplateId,
              input: questionInput,
            }),
          ).unwrap();

          if (!finalQuestionId) {
            console.error('Failed to create quiz question');
            return;
          }
        }

        const existingServerOptions = currentQuiz?.questions?.[0]?.options || [];
        const existingOptionMap = new Map<number, QuizAnswerOption>(
          existingServerOptions.map((opt) => [opt.id, opt]),
        );
        const existingOptionTextMap = new Map<string, QuizAnswerOption>(
          existingServerOptions.map((opt) => [opt.textContent.trim().toLowerCase(), opt]),
        );

        const processedOptionIds = new Set<number>();
        const processedOptionTexts = new Set<string>();

        for (const option of questionPayload.options || []) {
          if (!option?.textContent?.trim()) {
            continue;
          }

          const normalizedText = option.textContent.trim().toLowerCase();

          if (option.id && processedOptionIds.has(option.id)) {
            processedOptionTexts.add(normalizedText);
            continue;
          }

          if (processedOptionTexts.has(normalizedText)) {
            continue;
          }

          const trimmedTextContent = option.textContent.trim();
          const calculatedPosition = option.position ?? processedOptionIds.size;
          const correct = option.correct ?? false;

          if (option.id && existingOptionMap.has(option.id)) {
            const serverOption = existingOptionMap.get(option.id)!;
            const textChanged = serverOption.textContent.trim() !== trimmedTextContent;
            const positionChanged = (serverOption.position ?? 0) !== calculatedPosition;
            const correctChanged = (serverOption.correct ?? false) !== correct;

            if (textChanged || positionChanged || correctChanged) {
              const optionInput: QuizAnswerOptionInput = {
                textContent: trimmedTextContent,
                position: calculatedPosition,
                correct,
              };

              await dispatch(
                updateQuizAnswerOption({
                  workspaceId,
                  webinarId,
                  templateId: finalTemplateId,
                  questionId: finalQuestionId,
                  optionId: option.id,
                  input: optionInput,
                }),
              ).unwrap();
            }
            processedOptionIds.add(option.id);
            processedOptionTexts.add(normalizedText);
          } else if (!option.id) {
            const existingByText = existingOptionTextMap.get(normalizedText);

            if (existingByText) {
              const positionChanged = (existingByText.position ?? 0) !== calculatedPosition;
              const correctChanged = (existingByText.correct ?? false) !== correct;

              if (positionChanged || correctChanged) {
                const optionInput: QuizAnswerOptionInput = {
                  textContent: trimmedTextContent,
                  position: calculatedPosition,
                  correct,
                };

                await dispatch(
                  updateQuizAnswerOption({
                    workspaceId,
                    webinarId,
                    templateId: finalTemplateId,
                    questionId: finalQuestionId,
                    optionId: existingByText.id,
                    input: optionInput,
                  }),
                ).unwrap();
              }
              processedOptionIds.add(existingByText.id);
              processedOptionTexts.add(normalizedText);
            } else {
              const optionInput: QuizAnswerOptionInput = {
                textContent: trimmedTextContent,
                position: calculatedPosition,
                correct,
              };

              const optionId = await dispatch(
                createQuizAnswerOption({
                  workspaceId,
                  webinarId,
                  templateId: finalTemplateId,
                  questionId: finalQuestionId,
                  input: optionInput,
                }),
              ).unwrap();

              if (optionId) {
                processedOptionIds.add(optionId);
                processedOptionTexts.add(normalizedText);
              }
            }
          }
        }

        const updatedQuiz = await dispatch(
          fetchQuizTemplate({
            workspaceId,
            webinarId,
            templateId: finalTemplateId,
          }),
        ).unwrap();

        setQuizzes((prevQuizzes) =>
          prevQuizzes.map((quiz) =>
            quiz.id === quizId
              ? {
                  ...quiz,
                  existingQuiz: updatedQuiz,
                }
              : quiz,
          ),
        );
        if (!options?.silent) {
          toast.success(t('quizzes.quizUpdatedSuccess'));
        }
        setInnerAccordionValue('');
      }
    } catch (error) {
      console.error('Failed to persist quiz:', error);
      if (options?.silent) {
        // En mode lot, on laisse l'orchestrateur parent gérer l'erreur (un seul toast).
        throw error;
      }
      const err = error as AppError | { message?: string; code?: string; status?: number };
      const errorMessage = mapErrorMessage(err?.message, err?.code, err?.status);
      toast.error(t(errorMessage) || t('errors.generic') || 'Une erreur est survenue');
    } finally {
      setSubmittingQuizzes((prev) => ({ ...prev, [quizId]: false }));
      pendingOperationsRef.current.delete(quizId);
    }
  };

  // Sauvegarde de lot : valide tous les quizz modifiés, puis les persiste en séquence.
  const saveAll = async (): Promise<SaveAllResult> => {
    if (locked) return { ok: true, saved: 0 };

    // 1) Validation + capture des valeurs des quizz modifiés.
    // On capture AVANT de persister : créer un quizz met à jour le store, ce qui
    // reconstruit la liste locale et démonte les autres formulaires (donc leurs refs).
    const payloads: { id: string; values: unknown }[] = [];
    for (let index = 0; index < quizzes.length; index++) {
      const quiz = quizzes[index];
      const handle = itemRefs.current.get(quiz.id);
      if (!handle?.isDirty()) continue;
      const valid = await handle.validate();
      const values = handle.getValues() as { name?: string };
      if (!valid) {
        setMainAccordionOpen(QUIZ_ACCORDION_VALUE);
        setInnerAccordionValue(`item-${index}`);
        return { ok: false, reason: 'validation', label: values.name?.trim() || undefined };
      }
      payloads.push({ id: quiz.id, values });
    }

    if (payloads.length === 0) {
      return { ok: true, saved: 0 };
    }

    // 2) Persistance séquentielle à partir des valeurs capturées (chaque quizz reste
    // une cascade non atomique). On ne dépend plus des refs, qui peuvent être démontées.
    let failing: { id: string; values: unknown } | undefined;
    try {
      for (const payload of payloads) {
        failing = payload;
        await handleQuizSubmit(payload.values, payload.id, { silent: true });
        // En édition l'id ne change pas (pas de remount) : on remet le form à « non modifié ».
        itemRefs.current.get(payload.id)?.markPristine();
      }
    } catch (error) {
      console.error('Batch quiz save failed:', error);
      const label = (failing?.values as { name?: string } | undefined)?.name?.trim() || undefined;
      return { ok: false, reason: 'error', label };
    }

    return { ok: true, saved: payloads.length };
  };

  useImperativeHandle(
    ref,
    () => ({
      hasDirty: () => hasDirty,
      saveAll,
    }),
    // saveAll est recréée à chaque rendu ; on garde le handle aligné sur l'état courant.
    [hasDirty, quizzes],
  );

  const quizzesForAccordionFill = evergreenQuizIds
    ? quizTemplates.filter((template) => {
        const thankyouoffer = (template as any).thankyouoffer;
        const waitingRoomQuiz = template.waitingRoomQuiz;
        const base =
          (thankyouoffer === false || thankyouoffer === undefined || thankyouoffer === null) &&
          (waitingRoomQuiz === false || waitingRoomQuiz === undefined || waitingRoomQuiz === null);
        return base && evergreenQuizIds.has(template.id);
      })
    : quizTemplates;

  if (quizTemplatesLoading) {
    return (
      <div>
        <Accordion type="single" collapsible defaultValue="item-1">
          <AccordionItem
            value="item-1"
            className={`bg-white p-0 ${isEdit ? 'accordion-page' : ''}`}
          >
            <AccordionTrigger>
              <div className="flex flex-col  gap-2">
                <Title level={2}>{t('quizzes.sectionTitle')}</Title>
                <p className="text-sm font-normal text-tertiary">{t('quizzes.description')}</p>
              </div>
            </AccordionTrigger>
            <AccordionContent
              className={`flex flex-col gap-4 rounded-[20px] bg-white p-0 ${isEdit ? 'pb-4' : ''}`}
            >
              <div className="space-y-4">
                <Skeleton className="h-10 w-2/3" />
                <Skeleton className="h-28 w-full rounded-[20px]" />
                <Skeleton className="h-28 w-full rounded-[20px]" />
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        <div className="pt-4">
          <Skeleton className="h-10 w-40" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <Accordion
        type="single"
        collapsible
        value={mainAccordionOpen}
        onValueChange={setMainAccordionOpen}
      >
        <AccordionItem
          value={QUIZ_ACCORDION_VALUE}
          className={`bg-white p-0 ${isEdit ? 'accordion-page' : ''}`}
        >
          <AccordionTrigger fillForm={quizzesForAccordionFill.length > 0}>
            <div className="flex flex-col  gap-2">
              <Title level={2}>{t('quizzes.sectionTitle')}</Title>
              <p className="text-sm font-normal text-tertiary">{t('quizzes.description')}</p>
            </div>
          </AccordionTrigger>
          <AccordionContent
            className={`flex flex-col gap-4 rounded-[20px] bg-white p-0 ${isEdit ? 'pb-4 bg-subtitle' : ''}`}
          >
            {quizzes.length === 0 ? (
              <div
                onClick={() => {
                  if (!locked && (!isEvergreen || scheduling)) addQuiz();
                }}
                className={cn(locked && 'cursor-default opacity-90')}
              >
                <EmptyInputFile contentType={t('quizzes.emptyContentType')} type="quiz" />
              </div>
            ) : (
              <>
                <Accordion
                  type="single"
                  collapsible
                  className="space-y-[10px]"
                  value={innerAccordionValue}
                  onValueChange={handleInnerAccordionChange}
                >
                  {quizzes.map((quiz, index) => (
                    <div key={quiz.id}>
                      <QuizForm
                        ref={(el) => {
                          if (el) itemRefs.current.set(quiz.id, el);
                          else itemRefs.current.delete(quiz.id);
                        }}
                        quizIndex={index}
                        quizId={quiz.id}
                        existingQuiz={quiz.existingQuiz}
                        onRemove={() => {
                          void removeQuiz(quiz.id);
                        }}
                        onRemoveAnswerOption={(quizId, answerOptionId) => {
                          void removeAnswerOption(quizId, answerOptionId);
                        }}
                        canRemove={quizzes.length > 1 || !!quiz.existingQuiz}
                        onSubmit={(data) => handleQuizSubmit(data, quiz.id)}
                        isSubmitting={submittingQuizzes[quiz.id] || false}
                        isEvergreen={isEvergreen}
                        readOnly={readOnly}
                        batchMode={batchMode}
                        onDirtyChange={(dirty) => handleChildDirty(quiz.id, dirty)}
                      />
                      {videoUrl && (
                        <TimeCodeComponent
                          videoUrl={videoUrl}
                          scheduling={scheduling}
                          videoFallbackUrl={videoFallbackUrl}
                          selectedEventType="QUIZ"
                          selectedResourceId={quiz.existingQuiz?.id ?? null}
                          readOnly={locked}
                        />
                      )}
                    </div>
                  ))}
                </Accordion>
                {(!isEvergreen || !!scheduling) && !locked && quizzes.length < 10 && (
                  <button type="button" onClick={addQuiz} className="flex items-center gap-2 pt-4">
                    <Plus className="size-6" />
                    <Label className="cursor-pointer">{t('quizzes.addQuizButton')}</Label>
                  </button>
                )}
              </>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
});

export default QuizsForm;
